import { useContext, useEffect, useState } from 'react';

import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useRouter } from 'next/router';

import {
  localRoutes,
  defaultRoutes,
  usaRoutes,
  internationalRoutes,
  nonCurriculumRoutes,
  iPlanRoutes,
  routes,
} from '../constants/routes';
import PortalContext from '../context/portal/context';
import { Portal } from '../enums/portal.enum';
import { useGetPremiumRoutes } from '../hooks/useGetPremiumRoutes';
import { useSidebarNavigation } from '../hooks/useSidebarNavigation';
import useUpdatePinnedApps from '../hooks/useUpdatePinnedApps';
import { getFeaturePath } from '../utils/pathUtils';
import { SidebarLabel } from '../utils/portalMapping';
import { mapRouteToMenuItem } from '../utils/routeUtils';
import { useIdentifyUser } from '../utils/tracking';

import { Button } from './Button';
import ExpandableSection from './molecules/ExpandableSection';
import ModalAlerts from './organisms/Modal/ModalAlerts';

export default function Sidebar() {
  const router = useRouter();
  const currentPath = router.pathname;
  const { selectedPortal } = useContext(PortalContext);
  const isLocalPortal = selectedPortal === Portal.PHILIPPINES;
  const isInternationalPortal = selectedPortal === Portal.INTERNATIONAL;
  const isUSAPortal = selectedPortal === Portal.USA;
  useIdentifyUser();

  const [isMounted, setIsMounted] = useState(false);
  const premiumRoutes = useGetPremiumRoutes();
  const pinnedApps = useUpdatePinnedApps();
  const {
    handleNavigate,
    handleConfirm,
    showConfirmationModal,
    setShowConfirmationModal,
  } = useSidebarNavigation();

  const featurePath = getFeaturePath(currentPath);

  const portalLabel = SidebarLabel[selectedPortal];

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const getPortalRoutes = () => {
    if (isLocalPortal) {
      return localRoutes;
    }
    if (isInternationalPortal) {
      return internationalRoutes;
    }
    if (isUSAPortal) {
      return usaRoutes;
    }
    return defaultRoutes;
  };

  const features = isMounted
    ? getPortalRoutes().map((route) => mapRouteToMenuItem(route, premiumRoutes))
    : [];
  const nonCurriculumFeatures = nonCurriculumRoutes.map((route) =>
    mapRouteToMenuItem(route, premiumRoutes),
  );

  const iPlanFeatures = iPlanRoutes.map((route) =>
    mapRouteToMenuItem(route, premiumRoutes),
  );

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div className="relative flex-grow overflow-y-auto">
        <div className="flex flex-col gap-y-5">
          <nav className="mt-6 flex flex-col">
            <ul className="flex flex-col gap-y-7" role="list">
              <Button
                className="mx-3 flex flex-row items-center justify-center gap-2 rounded-md bg-blue-500 px-4 py-2 font-semibold text-white hover:bg-blue-600"
                onClick={() => handleNavigate(routes.TUTORIALS)}
              >
                <QuestionMarkCircleIcon className="h-5 w-5" />
                Tutorial
              </Button>
              {pinnedApps.length > 0 && (
                <ExpandableSection
                  allowHighlight={false}
                  featurePath={featurePath}
                  items={pinnedApps}
                  pinnedApps={pinnedApps}
                  title="Favorites"
                  onNavigate={handleNavigate}
                />
              )}
              <ExpandableSection
                featurePath={featurePath}
                items={features}
                pinnedApps={pinnedApps}
                title={`${portalLabel} Curriculum`}
                onNavigate={handleNavigate}
              />
              {isLocalPortal && (
                <ExpandableSection
                  featurePath={featurePath}
                  items={iPlanFeatures}
                  pinnedApps={pinnedApps}
                  title="IPlan (DepEd Region 7)"
                  titleLink={routes.IPLAN}
                  onNavigate={(route) => handleNavigate(route, true)}
                  onTitleClick={(route) => handleNavigate(route, true)}
                />
              )}
              <ExpandableSection
                featurePath={featurePath}
                items={nonCurriculumFeatures}
                pinnedApps={pinnedApps}
                title="More Features"
                onNavigate={handleNavigate}
              />
            </ul>
          </nav>
        </div>
        <div className="sticky inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent" />
      </div>
      <ModalAlerts
        handleConfirmClose={handleConfirm}
        setShowConfirmationModal={setShowConfirmationModal}
        showConfirmationModal={showConfirmationModal}
      />
    </div>
  );
}
